import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const TOKEN = () => localStorage.getItem('crm_token');
const HEADERS = () => ({ Authorization: `Bearer ${TOKEN()}` });

const STATUS_LABELS = {
  draft: { label: 'Taslak', badge: 'badge-gray' },
  running: { label: 'Gönderiliyor', badge: 'badge-primary' },
  paused: { label: 'Duraklatıldı', badge: 'badge-warning' },
  completed: { label: 'Tamamlandı', badge: 'badge-success' },
  cancelled: { label: 'İptal Edildi', badge: 'badge-danger' }
};

const EMPTY_FORM = {
  name: '',
  message_template: '',
  audience_mode: 'all',
  audience_tags: [],
  min_interval_seconds: 20,
  max_interval_seconds: 45,
  daily_limit: 150,
  send_window_start: '09:00',
  send_window_end: '20:00'
};

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [availableTags, setAvailableTags] = useState([]);
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState('');
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => { fetchCampaigns(); }, []);

  useEffect(() => {
    // Calisan (running) bir kampanya varsa listeyi kendiliginden tazeleyip
    // ilerlemeyi (gonderilen/basarisiz sayaci) canli goster.
    const hasRunning = campaigns.some(c => c.status === 'running');
    if (!hasRunning) return;
    const timer = setInterval(fetchCampaigns, 5000);
    return () => clearInterval(timer);
  }, [campaigns]);

  useEffect(() => {
    if (!detailCampaign) return;
    fetchDetail(detailCampaign.id);
    if (detailCampaign.status !== 'running') return;
    const timer = setInterval(() => fetchDetail(detailCampaign.id), 5000);
    return () => clearInterval(timer);
  }, [detailCampaign?.id]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get('/api/campaigns', { headers: HEADERS() });
      setCampaigns(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (id) => {
    try {
      const { data } = await axios.get(`/api/campaigns/${id}`, { headers: HEADERS() });
      setDetailData(data);
      setDetailCampaign(data.campaign);
    } catch (err) { console.error(err); }
  };

  const fetchTags = async () => {
    try {
      const { data } = await axios.get('/api/campaigns/tags', { headers: HEADERS() });
      setAvailableTags(data);
    } catch (err) { console.error(err); }
  };

  const fetchAudiencePreview = useCallback(async (mode, tags) => {
    setAudienceLoading(true);
    try {
      const { data } = await axios.post('/api/campaigns/audience-preview', {
        audience_filter: { mode, tags }
      }, { headers: HEADERS() });
      setAudienceCount(data.count);
    } catch (err) { console.error(err); }
    finally { setAudienceLoading(false); }
  }, []);

  useEffect(() => {
    if (!showModal) return;
    fetchAudiencePreview(form.audience_mode, form.audience_tags);
  }, [showModal, form.audience_mode, form.audience_tags, fetchAudiencePreview]);

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setAudienceCount(null);
    fetchTags();
    setShowModal(true);
  };

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      audience_tags: prev.audience_tags.includes(tag)
        ? prev.audience_tags.filter(t => t !== tag)
        : [...prev.audience_tags, tag]
    }));
  };

  const handleCreate = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Kampanya adı zorunludur.'); return; }
    if (!form.message_template.trim()) { setFormError('Mesaj metni zorunludur.'); return; }
    if (Number(form.max_interval_seconds) < Number(form.min_interval_seconds)) {
      setFormError('Maksimum bekleme, minimumdan küçük olamaz.');
      return;
    }
    setCreating(true);
    try {
      await axios.post('/api/campaigns', {
        name: form.name,
        message_template: form.message_template,
        audience_filter: { mode: form.audience_mode, tags: form.audience_tags },
        min_interval_seconds: form.min_interval_seconds,
        max_interval_seconds: form.max_interval_seconds,
        daily_limit: form.daily_limit,
        send_window_start: form.send_window_start,
        send_window_end: form.send_window_end
      }, { headers: HEADERS() });
      setShowModal(false);
      fetchCampaigns();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Kampanya oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/start`, {}, { headers: HEADERS() });
      fetchCampaigns();
    } catch (err) {
      alert(err.response?.data?.error || 'Kampanya başlatılamadı');
    }
  };

  const handlePause = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/pause`, {}, { headers: HEADERS() });
      fetchCampaigns();
    } catch (err) { alert(err.response?.data?.error || 'Duraklatılamadı'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Bu kampanya iptal edilsin mi? Bekleyen mesajlar gönderilmeyecek.')) return;
    try {
      await axios.post(`/api/campaigns/${id}/cancel`, {}, { headers: HEADERS() });
      fetchCampaigns();
    } catch (err) { alert(err.response?.data?.error || 'İptal edilemedi'); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Bu kampanya kalıcı olarak silinsin mi?')) return;
    try {
      await axios.delete(`/api/campaigns/${id}`, { headers: HEADERS() });
      fetchCampaigns();
    } catch (err) { alert(err.response?.data?.error || 'Silinemedi'); }
  };

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div>
      <div className="header">
        <div>
          <h1 className="page-title">Kampanyalar</h1>
          <p className="page-subtitle">Toplu WhatsApp mesajlarını spam korumalarına takılmadan, yavaş ve kontrollü şekilde gönderin.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>+ Yeni Kampanya</button>
      </div>

      {campaigns.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📣</div>
          <div className="empty-state-title">Henüz kampanya yok</div>
          <p className="text-muted">Müşterilerinize toplu WhatsApp mesajı göndermek için bir kampanya oluşturun.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {campaigns.map(c => {
            const st = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
            const progressPct = c.total_count > 0 ? Math.round(((c.sent_count + c.failed_count) / c.total_count) * 100) : 0;
            return (
              <div key={c.id} className="card" style={{ cursor: 'pointer' }} onClick={() => { setDetailCampaign(c); }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>{c.name}</h3>
                      <span className={`badge ${st.badge}`}>{st.label}</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {c.sent_count} gönderildi{c.failed_count > 0 ? `, ${c.failed_count} başarısız` : ''} / {c.total_count} toplam
                    </div>
                    <div style={{ marginTop: 8, height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', maxWidth: 320 }}>
                      <div style={{ height: '100%', width: `${progressPct}%`, background: c.failed_count > 0 ? 'linear-gradient(90deg,#10b981,#f43f5e)' : '#10b981', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                    {c.status === 'draft' && (
                      <button className="btn btn-primary btn-sm" onClick={() => handleStart(c.id)}>▶ Başlat</button>
                    )}
                    {c.status === 'running' && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => handlePause(c.id)}>⏸ Duraklat</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleCancel(c.id)}>İptal Et</button>
                      </>
                    )}
                    {c.status === 'paused' && (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => handleStart(c.id)}>▶ Devam Et</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleCancel(c.id)}>İptal Et</button>
                      </>
                    )}
                    {['draft', 'completed', 'cancelled'].includes(c.status) && (
                      <button className="btn btn-secondary btn-sm" onClick={(e) => handleDelete(c.id, e)}>🗑</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Yeni Kampanya</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {formError && <div className="badge badge-danger" style={{ display: 'block', marginBottom: 14, padding: '10px 14px' }}>{formError}</div>}

              <div className="form-group">
                <label className="form-label">Kampanya Adı</label>
                <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Örn: Yaz İndirimi Duyurusu" />
              </div>

              <div className="form-group">
                <label className="form-label">Mesaj Metni</label>
                <textarea className="textarea" rows={4} value={form.message_template} onChange={e => setForm({ ...form, message_template: e.target.value })} placeholder="Merhaba {isim}, size özel..." />
                <span className="form-helper">Kişiselleştirmek için metnin içine <code>{'{isim}'}</code> yazabilirsiniz — her müşteriye kendi adıyla gider.</span>
              </div>

              <div className="form-group">
                <label className="form-label">Hedef Kitle</label>
                <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input type="radio" checked={form.audience_mode === 'all'} onChange={() => setForm({ ...form, audience_mode: 'all', audience_tags: [] })} />
                    Tüm Müşteriler
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input type="radio" checked={form.audience_mode === 'tags'} onChange={() => setForm({ ...form, audience_mode: 'tags' })} />
                    Etikete Göre Seç
                  </label>
                </div>
                {form.audience_mode === 'tags' && (
                  availableTags.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>Müşterilerinizde henüz bir etiket tanımlı değil.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {availableTags.map(tag => (
                        <label key={tag} className={`badge ${form.audience_tags.includes(tag) ? 'badge-primary' : 'badge-gray'}`} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input type="checkbox" style={{ display: 'none' }} checked={form.audience_tags.includes(tag)} onChange={() => toggleTag(tag)} />
                          {tag}
                        </label>
                      ))}
                    </div>
                  )
                )}
                <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                  {audienceLoading ? 'Hesaplanıyor...' : audienceCount !== null && (
                    audienceCount === 0
                      ? '⚠️ Bu kriterlere uyan (pazarlama izni olan) müşteri yok.'
                      : `📨 ${audienceCount} müşteriye gönderilecek`
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Mesajlar Arası Bekleme (saniye)</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input className="input" type="number" min={5} style={{ width: 100 }} value={form.min_interval_seconds} onChange={e => setForm({ ...form, min_interval_seconds: e.target.value })} />
                  <span className="text-muted">—</span>
                  <input className="input" type="number" min={5} style={{ width: 100 }} value={form.max_interval_seconds} onChange={e => setForm({ ...form, max_interval_seconds: e.target.value })} />
                </div>
                <span className="form-helper">Her mesaj arasında bu aralıkta rastgele bir süre beklenir — WhatsApp'ın toplu/bot gönderimi fark edip numarayı kısıtlamasını önlemek için sabit değil rastgele aralık kullanılır. Düşük değerler riski artırır.</span>
              </div>

              <div className="form-group">
                <label className="form-label">Günlük Gönderim Limiti</label>
                <input className="input" type="number" min={1} style={{ width: 140 }} value={form.daily_limit} onChange={e => setForm({ ...form, daily_limit: e.target.value })} />
                <span className="form-helper">Kampanya bu sayıya ulaştığında o gün durur, ertesi gün otomatik devam eder.</span>
              </div>

              <div className="form-group">
                <label className="form-label">Gönderim Saatleri</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input className="input" type="time" style={{ width: 140 }} value={form.send_window_start} onChange={e => setForm({ ...form, send_window_start: e.target.value })} />
                  <span className="text-muted">—</span>
                  <input className="input" type="time" style={{ width: 140 }} value={form.send_window_end} onChange={e => setForm({ ...form, send_window_end: e.target.value })} />
                </div>
                <span className="form-helper">Bu saat aralığı dışında hiç mesaj gönderilmez (gece gönderimi de bir spam işaretidir).</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Vazgeç</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>{creating ? 'Oluşturuluyor...' : 'Kampanyayı Oluştur'}</button>
            </div>
          </div>
        </div>
      )}

      {detailCampaign && (
        <div className="modal-overlay" onClick={() => { setDetailCampaign(null); setDetailData(null); }}>
          <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{detailCampaign.name}</h3>
              <button className="modal-close" onClick={() => { setDetailCampaign(null); setDetailData(null); }}>×</button>
            </div>
            <div className="modal-body">
              {!detailData ? (
                <div className="loading"><div className="loading-spinner"></div></div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    {detailData.status_counts.map(sc => (
                      <span key={sc.status} className="badge badge-gray">{sc.status}: {sc.count}</span>
                    ))}
                  </div>
                  <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Müşteri</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Telefon</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left' }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.recipients.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '8px 12px' }}>{r.customer_name || '-'}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.phone}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {r.status === 'sent' && <span className="badge badge-success">Gönderildi</span>}
                              {r.status === 'pending' && <span className="badge badge-gray">Bekliyor</span>}
                              {r.status === 'failed' && <span className="badge badge-danger" title={r.error_message}>Başarısız</span>}
                              {r.status === 'skipped' && <span className="badge badge-gray">Atlandı</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setDetailCampaign(null); setDetailData(null); }}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Campaigns;
